import { ActionButton, ActionButtonType, BooleanRadioButton, BooleanRadioButtonGroup, Callout, List, PopoverBasicHelp } from "@octopusdeploy/design-system-components";
import type { GitCredentialRepositoryRestrictionsResource } from "@octopusdeploy/octopus-server-client";
import { useMemo, useState } from "react";
import type { ExpandableContainerProps } from "~/components/Expandable/index";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip/index";
import InputWithActions from "~/components/InputWithActions/index";
import { ExpandableFormSection, Note, Summary, Text } from "~/components/form/index";
type AllowedRepositoriesFormSectionProps = Omit<ExpandableContainerProps, "errorKey"> & {
    repositoryRestrictions: GitCredentialRepositoryRestrictionsResource;
    onRepositoryRestrictionsUpdated: (updatedRepositoryRestrictions: GitCredentialRepositoryRestrictionsResource) => void;
    showRepositoryRestrictionsWarning: boolean;
};
const GitAllowListHelpPopover = () => {
    const description = (<div>
            <p>The list of allowed repositories defines which Git repositories can use this credential.</p>
            <br />
            <p>Octopus also uses it to match Git credentials to Argo CD application sources.</p>
        </div>);
    return <PopoverBasicHelp placement="top-end" width="wide" description={description}/>;
};
export const AllowedRepositoriesFormSection = (props: AllowedRepositoriesFormSectionProps) => {
    const { repositoryRestrictions, onRepositoryRestrictionsUpdated, showRepositoryRestrictionsWarning, ...rest } = props;
    const [newAllowListEntry, setNewAllowListEntry] = useState<string>("");
    const [duplicateError, setDuplicateError] = useState<string>("");
    const allowedRepositoriesSummary = () => Summary.summary(repositoryRestrictions.Enabled ? "Only allow specified repositories" : "Allow all repositories");
    const allowedRepositoriesContainsHttpScheme = useMemo(() => repositoryRestrictions.AllowedRepositories.some((u) => u.toLowerCase().startsWith("http://")), [repositoryRestrictions.AllowedRepositories]);
    const handleInputChange = (value: string) => {
        setNewAllowListEntry(value);
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            setDuplicateError("");
        }
        else if (repositoryRestrictions.AllowedRepositories.some((repo) => repo.toLowerCase() === trimmedValue.toLowerCase())) {
            setDuplicateError("This repository URL is already in the list");
        }
        else {
            setDuplicateError("");
        }
    };
    const onEnabledUpdated = (enabled: boolean) => {
        onRepositoryRestrictionsUpdated({
            ...repositoryRestrictions,
            Enabled: enabled,
        });
    };
    const onAllowedRepositoriesUpdated = (newAllowedRepositories: string[]) => {
        onRepositoryRestrictionsUpdated({
            ...repositoryRestrictions,
            AllowedRepositories: newAllowedRepositories,
        });
    };
    const addAllowedRepository = () => {
        const trimmedEntry = newAllowListEntry.trim();
        const modifiedList = [...repositoryRestrictions.AllowedRepositories, trimmedEntry];
        onAllowedRepositoriesUpdated(modifiedList);
        setNewAllowListEntry("");
        setDuplicateError("");
    };
    const removeAllowedRepository = (index: number) => {
        const modifiedList = [...repositoryRestrictions.AllowedRepositories];
        modifiedList.splice(index, 1);
        onAllowedRepositoriesUpdated(modifiedList);
    };
    const updateAllowedRepository = (value: string, index: number) => {
        const modifiedList = [...repositoryRestrictions.AllowedRepositories];
        modifiedList[index] = value;
        onAllowedRepositoriesUpdated(modifiedList);
    };
    return (<ExpandableFormSection errorKey="allowedRepositories" title="Allowed Repositories" summary={allowedRepositoriesSummary()} help="Control which repositories are allowed to use this Git credential." {...rest}>
            {showRepositoryRestrictionsWarning && (<Callout type="warning" title="Are you sure you want to allow only specified Git repositories?">
                    This credential is currently being used by other repositories. Allowing this will restrict usage of this credential to the specified Git repositories and remove access for all others.
                </Callout>)}
            <BooleanRadioButtonGroup value={repositoryRestrictions.Enabled} onChange={(enabled) => onEnabledUpdated(enabled)}>
                <BooleanRadioButton value={false} label={<span>Allow all Git repositories</span>}/>
                <BooleanRadioButton value={true} label={<span>
                            Only allow specified Git repositories <GitAllowListHelpPopover />
                        </span>}/>
            </BooleanRadioButtonGroup>

            {repositoryRestrictions.Enabled && (<>
                    {allowedRepositoriesContainsHttpScheme && (<Callout hideTitle={true} type={"warning"}>
                            Use HTTPS to ensure secure connections. HTTP is supported, but not recommended.
                        </Callout>)}

                    <InputWithActions input={<Text value={newAllowListEntry} label="HTTPS Git repository URL" onChange={handleInputChange} error={duplicateError}/>} actions={<ActionButton label={"Add to list"} type={ActionButtonType.Secondary} onClick={addAllowedRepository} disabled={!newAllowListEntry.trim() || !!duplicateError}/>}/>
                    <Note>
                        This field supports wildcards (*) at the end of the URL e.g. <code>https://github.com/OctopusDeploy/*</code>
                    </Note>
                    <List items={repositoryRestrictions.AllowedRepositories} renderRow={({ item: url, index }) => (<InputWithActions input={<Text value={url} label="" onChange={(name) => updateAllowedRepository(name, index)}/>} actions={<IconButtonWithTooltip toolTipContent="Remove repository" icon={"XmarkIcon"} accessibleName={"Remove allowed repository"} onClick={() => removeAllowedRepository(index)}/>}/>)}/>
                </>)}
        </ExpandableFormSection>);
};
